import { useCallback, useMemo, useState } from "react";
import _ from "lodash";

import { AIIcon, LockIcon, NotesIcon } from "icons";
import { colors } from "theme/palette";
import { toSentenceCase } from "utils/common";

import Box from "components/Box";
import GroupLabel from "components/ButtonDropdown/GroupLabel";
import IconButton from "components/IconButton";
import Menu from "components/Menu";
import { EMPTY_VALUE_REGEX } from "components/RichTextEditor/constants";
import SanitizeHtml from "components/SanitizeHtml";
import Stack from "components/Stack";
import Text from "components/Text";
import Tooltip from "components/Tooltip";

import { useDueDiligenceFilters } from "pages/Deal/DueDiligence/Filters";

import { useDealChecklistPermission } from "entities/Deal/sdk";
import DropdownFieldRemarksDialog from "entities/Field/components/DropdownFieldRemarksDialog";
import FieldUpdateIcon from "entities/Field/components/FieldUpdateIcon";
import InstructionsDialogButton from "entities/Field/components/InstructionsDialogButton";
import LinkedFieldsDrawerButton from "entities/Field/components/LinkedFieldsDrawerButton";
import { EFieldStatus } from "entities/Field/constants";
import {
  FieldType,
  IField,
  markFieldSeenBySignedUser,
} from "entities/Field/sdk";
import FieldStatusIcon from "entities/Package/components/FieldStatusIcon";
import StatusSelector from "entities/Package/components/StatusSelector";

interface IFieldTitle {
  field: IField;
  dealId: number;
  packageId: number;
  onUpdate: ({ message }: { message?: string }) => void;
}

const FieldTitle = ({ field, dealId, packageId, onUpdate }: IFieldTitle) => {
  const [markAsSeenLoading, setMarkAsSeenLoading] = useState(false);
  const [isRemarksDialogOpen, setIsRemarksDialogOpen] = useState(false);
  const [statusMenuAnchorEl, setStatusMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);

  const { showApprovalBoxes } = useDueDiligenceFilters();
  const { data: userPermission } = useDealChecklistPermission({
    dealId,
    packageId,
  });

  const hasEditAccess = useMemo(
    () => !_.isNil(userPermission) && userPermission.has_write_access,
    [userPermission]
  );

  const handleClickUpdatedTag = useCallback(() => {
    if (markAsSeenLoading) {
      return;
    }

    setMarkAsSeenLoading(true);
    markFieldSeenBySignedUser({ dealId, packageId, fieldId: field.field_id })
      .then(() => onUpdate({}))
      .finally(() => setMarkAsSeenLoading(false));
  }, [dealId, packageId, field, onUpdate, markAsSeenLoading]);

  const showAIIcon = useMemo(
    () =>
      field.field_type === FieldType.FILE &&
      field.custom_values.some((item) => !_.isEmpty(item.file?.openai_files)),
    [field]
  );

  const userCanModifyStatus = useMemo(() => {
    if (
      [EFieldStatus.IN_REVIEW, EFieldStatus.SATISFIED].includes(field.status)
    ) {
      return userPermission?.is_checklist_owner;
    }

    if (field.status === EFieldStatus.NA) {
      return userPermission?.is_full_access;
    }

    return true;
  }, [userPermission, field]);

  const sharedRemark = useMemo(
    () => _.first(field.shared_remarks),
    [field.shared_remarks]
  );
  const privateRemark = useMemo(
    () => _.first(field.private_remarks),
    [field.private_remarks]
  );

  const handleCloseRemarks = useCallback(
    () => setIsRemarksDialogOpen(false),
    []
  );
  const handleOpenRemarks = useCallback(() => setIsRemarksDialogOpen(true), []);

  const sharedRemarkFormattedValue = useMemo(() => {
    if (!_.isNil(sharedRemark)) {
      return EMPTY_VALUE_REGEX.test(sharedRemark.shared_remark)
        ? ""
        : sharedRemark.shared_remark;
    }
    return "";
  }, [sharedRemark]);

  const privateRemarkFormattedValue = useMemo(() => {
    if (!_.isNil(privateRemark)) {
      return EMPTY_VALUE_REGEX.test(privateRemark.private_remark)
        ? ""
        : privateRemark.private_remark;
    }
    return "";
  }, [privateRemark]);

  return (
    <Stack spacing={0.5} sx={{ flex: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        {showApprovalBoxes && hasEditAccess && (
          <Tooltip
            title={
              <>
                <Text variant="text4" color={colors.gray40}>
                  Current status
                </Text>
                <Text variant="text4" fontWeight={600}>
                  {toSentenceCase({ text: field.status })}
                </Text>
                {!userCanModifyStatus && (
                  <Text variant="text4">🚫 You can’t modify this status</Text>
                )}
              </>
            }
            placement="left"
          >
            <span>
              <IconButton
                sx={{ opacity: 1 }}
                disabled={!userCanModifyStatus}
                onClick={() => setIsStatusMenuOpen(true)}
                ref={(el) => setStatusMenuAnchorEl(el)}
                dataTestid="field-status-button"
              >
                <FieldStatusIcon status={field.status} />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Menu
          keepMounted={false}
          open={isStatusMenuOpen}
          anchorEl={statusMenuAnchorEl}
          onClose={() => setIsStatusMenuOpen(false)}
          onClick={() => setIsStatusMenuOpen(false)}
        >
          <GroupLabel label="status" />
          <StatusSelector
            dealId={dealId}
            fieldIds={[field.field_id]}
            companyPackageId={packageId}
            successCallback={onUpdate}
          />
        </Menu>

        <Stack direction="row" spacing={0.5} alignItems="center">
          {field.single_persona && <LockIcon />}
          <Text
            variant="text2"
            sx={{
              opacity: field.status === EFieldStatus.NA ? 0.5 : 1,
            }}
            fontWeight={500}
            data-testid="dd-field-name"
            title={field.display_name}
          >
            {field.display_name}
          </Text>
          <InstructionsDialogButton
            internalInstructions={field?.internal_instructions}
            externalInstructions={field?.external_instructions}
          />
          {field?.is_linked && (
            <LinkedFieldsDrawerButton
              isOverridden={field?.is_overridden}
              dealId={dealId}
              packageId={packageId}
              fieldId={field.field_id}
              onUpdate={() => onUpdate({})}
            />
          )}
          {showAIIcon && <AIIcon style={{ color: colors.blue100 }} />}
          {field.has_new && (
            <FieldUpdateIcon
              handleClick={handleClickUpdatedTag}
              disabled={markAsSeenLoading}
              tooltip="Field has updates"
            />
          )}
        </Stack>
      </Stack>

      {(!_.isNil(sharedRemark) || !_.isNil(privateRemark)) && (
        <Stack spacing={0.5} paddingLeft={4} data-testid="field-remarks">
          {!_.isEmpty(privateRemarkFormattedValue) && (
            <Stack
              sx={{
                cursor: hasEditAccess ? "pointer" : "inherit",
                width: "fit-content",
                maxWidth: "60%",
              }}
              onClick={handleOpenRemarks}
            >
              <Tooltip
                title={
                  <Box
                    sx={{
                      "& *": {
                        // The SanitizeHtml component includes predefined CSS rules that override custom styles.
                        // Therefore we use "!important"
                        fontSize: "12px !important",
                      },
                    }}
                  >
                    <Text fontWeight={700} variant="text4">
                      Reviewer Remarks
                    </Text>
                    <SanitizeHtml html={privateRemarkFormattedValue} />
                  </Box>
                }
                placement="top-start"
                key={`${isRemarksDialogOpen}-private-remark`}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    ul: {
                      margin: 0,
                    },
                    ol: {
                      margin: 0,
                    },
                  }}
                >
                  <Stack
                    sx={{
                      backgroundColor: colors.pink10,
                    }}
                    borderRadius="10px"
                    alignItems="center"
                    justifyContent="center"
                    height="20px"
                    width="20px"
                  >
                    <NotesIcon
                      style={{ width: 16, height: 16, color: colors.pink100 }}
                    />
                  </Stack>

                  <SanitizeHtml
                    html={privateRemarkFormattedValue}
                    sx={{
                      fontStyle: "italic",
                      paddingRight: 1.5,
                      wordBreak: "break-word",
                    }}
                    truncateLines={1}
                  />
                </Stack>
              </Tooltip>
            </Stack>
          )}
          {!_.isEmpty(sharedRemarkFormattedValue) && (
            <Stack
              sx={{
                cursor: hasEditAccess ? "pointer" : "inherit",
                width: "fit-content",
                maxWidth: "60%",
              }}
              onClick={handleOpenRemarks}
            >
              <Tooltip
                title={
                  <Box
                    sx={{
                      "& *": {
                        // The SanitizeHtml component includes predefined CSS rules that override custom styles.
                        // Therefore we use "!important"
                        fontSize: "12px !important",
                      },
                    }}
                  >
                    <Text fontWeight={700} variant="text4">
                      {userPermission?.is_checklist_owner &&
                      userPermission?.is_full_access
                        ? "Public "
                        : ""}
                      Remarks
                    </Text>
                    <SanitizeHtml html={sharedRemarkFormattedValue} />
                  </Box>
                }
                placement="top-start"
                key={`${isRemarksDialogOpen}-shared-remark`}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    ul: {
                      margin: "0px",
                    },
                    ol: {
                      margin: "0px",
                    },
                  }}
                >
                  <Stack
                    sx={{
                      backgroundColor: colors.blue10,
                    }}
                    borderRadius="10px"
                    alignItems="center"
                    justifyContent="center"
                    height="20px"
                    width="20px"
                  >
                    <NotesIcon
                      style={{
                        width: 16,
                        height: 16,
                        color: colors.blue100,
                      }}
                    />
                  </Stack>
                  <SanitizeHtml
                    html={sharedRemarkFormattedValue}
                    sx={{
                      fontStyle: "italic",
                      paddingRight: 1.5,
                      wordBreak: "break-word",
                    }}
                    truncateLines={1}
                  />
                </Stack>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      )}

      {hasEditAccess && !_.isNil(userPermission) && (
        <DropdownFieldRemarksDialog
          showPrivateRemarks={
            userPermission.is_checklist_owner && userPermission.is_full_access
          }
          open={isRemarksDialogOpen}
          handleClose={handleCloseRemarks}
          dealId={dealId}
          fieldId={field.field_id}
          onUpdate={onUpdate}
          sharedRemark={sharedRemark}
          privateRemark={privateRemark}
          fieldName={field.display_name}
          packageId={packageId}
        />
      )}
    </Stack>
  );
};

export default FieldTitle;
